// @ts-nocheck
import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ChevronDown,
  ChevronRight,
  Loader2,
  CheckCircle2,
  AlertCircle,
  Table,
  Tag,
  Search,
  Calculator,
  FileText,
  Code,
  Sigma,
  Combine,
} from 'lucide-react';
import { renderToolResult, summarizeToolResult } from './toolRenderers';

const TOOL_META = {
  extract_pdf: { label: 'Read PDF', icon: FileText, color: '#dc2626' },
  consolidate_workbooks: { label: 'Combine workbooks', icon: Combine, color: '#0d9488' },
  get_sheet_structure: { label: 'Inspect sheet', icon: Table, color: '#3b82f6' },
  find_net_income: { label: 'Find Net Income', icon: Search, color: '#8b5cf6' },
  read_rows: { label: 'Read rows', icon: FileText, color: '#0ea5e9' },
  classify_section: { label: 'Classify section', icon: Tag, color: '#10b981' },
  detect_totals: { label: 'Detect totals', icon: Sigma, color: '#f59e0b' },
  verify_net_profit: { label: 'Verify Net Profit', icon: Calculator, color: '#ef4444' },
  run_python: { label: 'Run Python', icon: Code, color: '#6366f1' },
};

const StatusIcon = ({ status }) => {
  if (status === 'running') return <Loader2 size={12} className="spin" />;
  if (status === 'error') return <AlertCircle size={12} color="#dc2626" />;
  return <CheckCircle2 size={12} color="#16a34a" />;
};

const ToolCallBlock = ({ calls, isRunning }) => {
  // Default open on mount — covers both live runs (mounted while running)
  // and restored past sessions (mounted with isRunning already false).
  // Users can still collapse manually.
  const [expanded, setExpanded] = useState(true);
  const [openCallId, setOpenCallId] = useState(null);

  const verb = isRunning ? 'Running' : 'Used';
  const noun = calls.length === 1 ? 'tool' : 'tools';

  return (
    <div className="tool-block">
      <button
        type="button"
        className="tool-block-toggle"
        onClick={() => setExpanded((e) => !e)}
      >
        {isRunning ? (
          <Loader2 size={12} className="spin" />
        ) : expanded ? (
          <ChevronDown size={12} />
        ) : (
          <ChevronRight size={12} />
        )}
        <span>
          {verb} {calls.length} {noun}
        </span>
      </button>

      <AnimatePresence initial={false}>
        {expanded && (
          <motion.div
            className="tool-block-body"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.18 }}
          >
            <div className="tool-block-list">
              {calls.map((call) => {
                const meta = TOOL_META[call.toolName] || {
                  label: call.toolName,
                  icon: Sigma,
                  color: '#6b7280',
                };
                const Icon = meta.icon;
                const isOpen = openCallId === call.toolUseId;
                const summary = summarizeToolResult(call.toolName, call.result, call.input);
                const hasDetail = call.result != null;

                return (
                  <div
                    key={call.toolUseId}
                    className={`tool-call-row ${call.status}`}
                  >
                    <button
                      type="button"
                      className="tool-call-header"
                      disabled={!hasDetail}
                      onClick={() =>
                        setOpenCallId(isOpen ? null : call.toolUseId)
                      }
                    >
                      <Icon size={13} style={{ color: meta.color }} />
                      <span className="tool-call-label">{meta.label}</span>
                      {summary && (
                        <span className="tool-call-summary">→ {summary}</span>
                      )}
                      <div className="tool-call-spacer" />
                      <StatusIcon status={call.status} />
                      {hasDetail && (
                        isOpen ? <ChevronDown size={11} /> : <ChevronRight size={11} />
                      )}
                    </button>

                    <AnimatePresence initial={false}>
                      {isOpen && hasDetail && (
                        <motion.div
                          className="tool-call-detail"
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.18 }}
                        >
                          {renderToolResult(call.toolName, call.result, call.input)}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                );
              })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ToolCallBlock;
